import { KatapultElement, html } from '../../mixins/katapult-element';
import { AnimationManager } from './animationManager';

import '@polymer/paper-tabs/paper-tabs.js';
import '@polymer/paper-tabs/paper-tab.js';
import '@polymer/paper-radio-button/paper-radio-button.js';
import '@polymer/paper-radio-group/paper-radio-group.js';
import '@polymer/paper-toggle-button/paper-toggle-button.js';

import '../input-element/input-element';
import '../bind-path/bind-path';
import './katapult-editor-step';
import { DefaultStyle } from './katapult-model-editor-style';
import { NameToKey } from '../../modules/NameToKey';
import { CamelCase } from '../../modules/CamelCase.js';
import { Path } from '../../modules/Path.js';
import { IllegalCharacters, ContainsIllegalCharacters } from '../../modules/ContainsIllegalCharacters.js';
import { ToArray } from '../../modules/ToArray.js';
import { GeneratePushID } from '../../modules/GeneratePushID';
import { Debounce } from '../../modules/Debounce';
import { KLogic } from '../../modules/KLogic.js';
import '../katapult-elements/katapult-button.js';

function createNewPicklistItem(e) {
  e.currentTarget.shadowRoot.getElementById('element').close();
  document.dispatchEvent(new CustomEvent('create-new-picklist-item', { detail: e.detail }));
}

export class KatapultModelEditorElement extends KatapultElement {
  add(a, b) {
    return a + b;
  }

  /**
   * @param {Event|String} e - The trigger event or items path.
   * @param {Object} item - The type of item to add.
   */
  addItem(e, item) {
    const isEvent = e instanceof Event;
    const path = isEvent ? this.getDataPath(e) : e;
    const _item = isEvent ? {} : item;
    // Call update property.
    this.updateProperty(new CustomEvent('add-item', { detail: { path: Path.join([path, GeneratePushID()]), value: _item } }));
    return _item;
  }

  /**
   *
   * @param {Event|String} e - The trigger event or items path.
   * @param {Object} item - The type of item to add.
   */
  addOrderedItem(e, item) {
    const isEvent = e instanceof Event;
    const _type = isEvent ? e.currentTarget?.dataset?.type : null;
    const path = isEvent ? this.getDataPath(e) : e;
    const items = Path.get(this, path);
    const itemsArray = this.getOrderedItemsArray(items);
    const order = itemsArray.reduce((acc, x) => (acc = acc < x.order + 1 ? x.order + 1 : acc), 0);
    const _item = isEvent ? { order } : { ...item, order };
    switch (_type) {
      case 'warning':
        _item.applies_to = 'APP';
        break;
    }
    const pushId = GeneratePushID();
    delete _item.$key;
    // Call update property.
    this.updateProperty(new CustomEvent('add-item', { detail: { path: Path.join([path, pushId]), value: _item } }));
    return _item;
  }

  and(a, b) {
    return Array.from(arguments).every((x) => x);
  }

  camelCase(x) {
    return CamelCase(x);
  }

  camelCaseKey(x) {
    if (x) return CamelCase(x.$key);
    return '';
  }

  getItemsArray(...args) {
    // Convert change event objects to their base values.
    args = args.map((x) => (['base', 'path', 'value'].every((p) => x?.hasOwnProperty(p)) ? x.base : x));
    // Pass into toArray().
    let res = ToArray(...args);
    // Filter out null values.
    res = res.filter((x) => x != null && (!x.hasOwnProperty('$val') || x.$val != null));
    // Return result.
    return res;
  }

  getOrderedItemsArray(...args) {
    let res = this.getItemsArray(...args);
    // Sort based on order if present.
    res.sort((a, b) => a.order - b.order);
    return res;
  }

  orderedItemsArraySortChanged(e) {
    const path = this.getDataPath(e);
    const items = this.get(path);
    const itemsArray = this.getOrderedItemsArray(items);
    const range = e.detail.range.sort((a, b) => a - b);
    const update = {};
    // Loop through items and adjust each item's order.
    itemsArray.forEach((item, i) => {
      const itemOrderPath = Path.join([path, item.$key, 'order']);
      // If this is the item we dragged, move it to it's new index.
      if (i == e.detail.prevIndex) update[itemOrderPath] = e.detail.index;
      // Or, if this is an item affected by the drag, shift it's order appropriately.
      else if (range[0] <= i && i <= range[1]) update[itemOrderPath] = i + e.detail.shift;
      // Othwise, leave it alone (also serves to fix bad/missing order values).
      else update[itemOrderPath] = i;
    });
    for (const path in update) this.updateProperty(new CustomEvent('sort-changed', { detail: { path, value: update[path] } }));
  }

  updateProperty(e) {
    // Remap the event path based on event type.
    switch (e.type) {
      case 'opened-changed':
        e.detail.path = '$opened';
        break;
      case 'render-changed':
        e.detail.path = '$render';
        break;
    }
    super.updateProperty(e);
  }
}

export class KatapultEditorFrame extends KatapultModelEditorElement {
  static get style() {
    return html`
      ${DefaultStyle}
      <style>
        :host {
          position: relative;
          height: 100%;
          width: 100%;
          padding: 24px;
          box-sizing: border-box;
          overflow-y: auto;
        }
      </style>
    `;
  }
}

export class KatapultEditorTemplate extends KatapultEditorFrame {
  static get style() {
    return html`
      ${super.style}
      <style>
        #loadingOverlay {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 100%;
          z-index: 999;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.6);
        }
      </style>
      <div id="loadingOverlay"><paper-spinner-lite active></paper-spinner-lite></div>
    `;
  }

  static get createItemContainer() {
    return html`
      <div id="finishCreatingContainer" style="display: flex; justify-content: center; padding: 64px;">
        <katapult-button
          color="var(--secondary-color)"
          on-click="promptSaveItem"
          loading="[[savingItem]]"
          disabled="[[!canSaveItem]]"
          icon="new_releases"
          >[[getCreateItemButtonTitle(createItemButtonTitle, itemTypeTitles.title)]]</katapult-button
        >
      </div>
    `;
  }

  static get properties() {
    return {
      activeSteps: {
        type: Object,
        value: {}
      },
      canSaveItem: {
        type: Boolean,
        value: false,
        notify: true
      },
      creatingItem: {
        type: Boolean,
        notify: true,
        readOnly: true,
        value: false
      },
      externalChanges: {
        type: Boolean,
        notify: true,
        readOnly: true,
        value: false
      },
      inputTypes: {
        type: Object,
        value: {
          textbox: 'Single Line Text',
          link: 'Hyperlink',
          textbox_commit: 'Single Line Text with Commit',
          textarea: 'Multi-Line Text',
          checkbox: 'Checkbox',
          dropdown: 'Dropdown',
          multi_dropdown: 'Multi-Select Dropdown',
          group: 'Group',
          table: 'Table',
          date: 'Date',
          file: 'File'
        }
      },
      itemData: {
        type: Object,
        readOnly: true
      },
      _itemData: {
        type: Object
      },
      itemDataReference: {
        type: Object,
        readOnly: true
      },
      itemKey: {
        type: String,
        readOnly: true
      },
      itemNewKeyExists: {
        type: Boolean,
        value: false
      },
      katapultDropDownFitInto: {
        type: Boolean,
        value: true,
        reflectToAttribute: true
      },
      katapultDropDownScrollTarget: {
        type: Boolean,
        value: true,
        reflectToAttribute: true
      },
      label: {
        type: String,
        notify: true
      },
      labelAllowAllCharacters: {
        type: Boolean,
        value: false
      },
      labelContainsIllegalCharacters: {
        type: Boolean,
        value: false
      },
      labelErrorMessage: {
        type: String,
        notify: true
      },
      lastModifiedPath: {
        type: String,
        value: 'last_modified'
      },
      loading: {
        type: Boolean,
        value: true,
        notify: true
      },
      numSteps: {
        type: Number,
        value: Infinity,
        readOnly: true
      },
      savingItem: Boolean,
      steps: {
        type: Array,
        value: []
      },
      currentIndex: {
        type: Number,
        value: 0,
        notify: true,
        readOnly: true
      },
      unsavedChanges: {
        type: Boolean,
        notify: true,
        readOnly: true,
        value: false
      }
    };
  }

  static get observers() {
    return [
      'itemKeyChanged(itemKey)',
      'itemDataChanged(itemData.*)',
      '_itemDataChanged(_itemData.*)',
      'itemDataReferenceChanged(itemDataReference.*)',
      'updateExternalChanges(flattenedItemData, flattenedItemDataReference)',
      'updateUnsavedChanges(flattenedItemDataReference, _flattenedItemData)',
      'updateFinishedSteps(currentIndex, numSteps)',
      '_labelChanged(_itemData.$label)',
      'updateItemNewKeyExists(_itemData.$key, itemDataReference.$key, categoryData.*, computedLabel.*)',
      'checkLabelDependencies(_itemData.*)',
      'updateCanSaveItem(itemNewKeyExists, unsavedChanges, labelContainsIllegalCharacters, _itemData.*, categoryDetails.showList, computedLabel.valid)',
      'updateLabelErrorMessage(itemNewKeyExists, labelContainsIllegalCharacters, _itemData.$key, _itemData.$label, _labelErrorMessage, computedLabel.valid)'
    ];
  }

  constructor() {
    super();
    this.$true = true;
    this.$this = this;
    this.camelCase = CamelCase;
    this.createItemButtonTitle = null;

    this.debouncedItemDataChanged = Debounce(
      () => (this.flattenedItemData = Path.flattenObject(this.itemData, null, { validate: this.flattenedDataValidate.bind(this) }))
    );
    this._debouncedItemDataChanged = Debounce(() => {
      this._flattenedItemData = Path.flattenObject(this._itemData, null, { validate: this.flattenedDataValidate.bind(this) });
      this.checkSteps();
    });
    this.debouncedItemDataReferenceChanged = Debounce(
      () =>
        (this.flattenedItemDataReference = Path.flattenObject(this.itemDataReference, null, {
          validate: this.flattenedDataValidate.bind(this)
        }))
    );
  }

  ready() {
    super.ready();
    this.createNewPicklistItem = createNewPicklistItem;
    let anim = new AnimationManager(this);
    anim.register(this.$.finishCreatingContainer, {
      observer: 'creatingItem, finishedSteps',
      rules: [
        {
          if: true,
          style: {
            $hidden: true,
            $invisible: true,
            transform: 'translateY(24px)'
          }
        },
        {
          if: (creatingItem, finishedSteps) => creatingItem && finishedSteps,
          style: {
            $hidden: false,
            $invisible: false,
            transform: 'none'
          }
        }
      ]
    });
    anim.register(this.$.loadingOverlay, {
      observer: 'loading',
      rules: [
        {
          if: true,
          style: { $invisible: true }
        },
        {
          if: (loading) => loading,
          style: { $invisible: false }
        }
      ]
    });
  }

  checkSteps() {
    /* Intended to be overwritten by classes extending this class */
    // Update states.
    let stepIndex = 0;
    this.steps.forEach((step, i) => {
      // Set state active flag.
      let active = true;
      if (typeof step.if === 'function') active = step.if();
      this.set(`steps.${i}.active`, active);
      // Set state index.
      if (active) {
        this.set(`steps.${i}.index`, stepIndex++);
      } else {
        this.set(`steps.${i}.index`, null);
      }
      // Run step trigger.
      if (step.index != null && step.index == this.currentIndex && typeof step.trigger === 'function' && step.trigger()) this.nextStep();
    });
    this._setNumSteps(stepIndex);
  }

  compareFlattenedData(a, b) {
    if (a && b) {
      let aKeys = Object.keys(a).filter((x) => (!x.includes('$') && !x.includes(this.lastModifiedPath)) || ['$key', '$label'].includes(x));
      let bKeys = Object.keys(b).filter((x) => (!x.includes('$') && !x.includes(this.lastModifiedPath)) || ['$key', '$label'].includes(x));
      let mismatchedKeys = bKeys.filter((key) => b[key] != a[key]);
      return { mismatchedKeys, changed: bKeys.length != aKeys.length || mismatchedKeys.length > 0 };
      // if (bKeys.length != aKeys.length) return true;
      // let diff = bKeys.find(key => b[key] != a[key]);
      // return diff != null;
    }
  }

  createItem() {
    // Enter creating mode.
    this._setCreatingItem(true);
    // Reset step index to 0.
    this._setCurrentIndex(0);
  }

  async delete() {
    await this._save(null);
  }

  discardChanges() {
    this._resetInternalItemData();
  }

  equal(a, b) {
    return a == b;
  }

  flattenedDataValidate(val, valKey, parentObject) {
    // The user might be setting a literal empty string as part of a KLogic block, which is valid and should be preserved.
    if (val === '' && parentObject && KLogic.isKlogicBlock(parentObject)) {
      return true;
    }
    // If the key is value, an empty pole_tag key, or the attributes inside measured_heights then we should allow it if it's an empty string
    const allowedEmptyKeys = ['value', 'tagtext', 'company', 'cable_type', 'equipment_type', 'manual_height'];
    if (allowedEmptyKeys.includes(valKey) || this.categoryDetails.category == 'photo_routines' || this.allowEmptyStrings) {
      return !Path.valueIsEmpty(val);
    }
    // Otherwise, don't allow any empty object, null, undefined, or empty string
    return !Path.valueIsEmpty(val) && val !== '';
  }

  exitCreateItem() {
    // Exit creating mode.
    this._setCreatingItem(false);
    // Reset internal item data to match any external data.
    this._resetInternalItemData();
  }

  _initializeNewItem() {
    /* Override */
  }

  isArray(x) {
    return Array.isArray(x);
  }

  itemKeyChanged() {
    if (this.itemKey == null) {
      this.setItemData(null, true);
      if (this.cleanup) this.cleanup();
    }
  }

  itemDataChanged(changeRecord) {
    this.debouncedItemDataChanged(changeRecord);
  }

  _itemDataChanged(changeRecord) {
    this._debouncedItemDataChanged(changeRecord);
  }

  itemDataReferenceChanged(changeRecord) {
    this.debouncedItemDataReferenceChanged(changeRecord);
  }

  _nameToKey(x) {
    return NameToKey(this.label, { keepCase: this.allowUpperCaseKeys });
  }

  _labelChanged() {
    this.label = this.get('_itemData.$label');
    if (this.label) {
      this.labelChanged(this.label);
    }
    if (this.keyMatchLabel) {
      this.labelContainsIllegalCharacters = this.labelAllowAllCharacters ? false : ContainsIllegalCharacters(this.label);
      let key = null;
      if (this.label && !this.labelContainsIllegalCharacters) key = this._nameToKey(this.label);
      this.set('_itemData.$key', key);
    }
  }

  labelChanged() {
    /* Override */
  }

  nextStep(options = {}) {
    if (this.creatingItem) {
      this._setCurrentIndex(this.currentIndex + 1);
      if (!options.noScroll) {
        setTimeout(() => {
          this.scrollTo({ top: this.scrollHeight, behavior: 'smooth' });
        });
      }
    }
  }

  nullify(x) {
    return Path.nullify(x);
  }

  _resetInternalItemData() {
    if (this.itemData != null) this.setInternalItemData(Path.copy(this.itemData));
    else this.setInternalItemData(this.defaultItemData ?? null);
  }

  promptSaveItem() {
    this.dispatchEvent(new CustomEvent('save-item'));
  }

  getInternalItemData() {
    return this._itemData;
  }

  setInternalItemData(data) {
    this._itemData = data;
  }

  setItem(itemKey, itemData, _itemData) {
    // Update item key.
    this._setItemKey(itemKey);

    this.loading = true;

    setTimeout(() => {
      // Update item data.
      if (itemData !== undefined) {
        this.setItemData(itemData, true);
        if (_itemData) this.setInternalItemData(_itemData);
        else this._resetInternalItemData();
        if (this.creatingItem) {
          this._initializeNewItem();
        }
      }
      setTimeout(() => {
        this.loading = false;
      });
    }, 100);

    this.dispatchEvent(new CustomEvent('item-set', { detail: { itemData, itemKey } }));
  }

  beforeSave() {
    /* Override */
  }

  afterSave() {
    /* Override */
  }

  buttonSet(e, value) {
    let path = e.currentTarget.getAttribute('path') || e.currentTarget.path;
    if (this.get(path) != value) {
      this.set(path, value);
    } else {
      this.checkSteps();
    }
  }
  buttonSetFalse(e) {
    this.buttonSet(e, false);
  }
  buttonSetTrue(e) {
    this.buttonSet(e, true);
  }

  async save() {
    await this.beforeSave();
    await this._save(this._itemData);
  }

  async _save(itemData, update) {
    if (this.dataPath == null) throw 'Cannot save this item. The data path has not been properly set';

    if (this.categoryDetails == null) throw `Cannot save this item. The editor is missing category information`;

    // Initialize the update object.
    update = update || {};

    // If item data is null, we are deleting this item.
    let deleting = itemData == null;
    let itemKey, newKey, previousKey, keyChanged;

    if (this.categoryDetails.showList) {
      // Get the key for this item.  The reason we don't use this.itemKey is because the key could possibly change as a result of the user's edit (i.e. an attribute key changing if the name is changed).
      // If we are deleting the item, we will just use the previous key below.
      newKey = Path.get(itemData, '$key');
      if (newKey == null && !deleting) throw 'New key is missing';

      // Get the previous key so we can see if the key has changed.  If it has, remove the data at the previous key.
      previousKey = this.get('itemDataReference.$key');

      // If we are deleting, the previous key must exist.
      if (previousKey == null && deleting) throw 'Previous key is missing.';

      keyChanged = newKey != null && previousKey != null && newKey != previousKey;

      // Get the current or new key for this item.
      itemKey = deleting ? previousKey : newKey;
      if (itemKey == null) throw 'Item key is missing';
    }

    await this._saveUpdate(itemData, null, { newKey, previousKey, itemKey, keyChanged, deleting });

    // Clear up_to_date flag on any shared instances of this model.
    await FirebaseWorker.ref(`photoheight/company_space/${this.modelSpaceDetails.$key}/sharing_links/model_sharing`)
      .once('value')
      .then(async (s) => {
        const update = {};
        Object.entries(s.val() ?? {}).forEach(([linkId, sharingLink]) => {
          if (sharingLink.sender?.company_id == this.modelSpaceDetails.$key) update[`${linkId}/metadata/up_to_date`] = false;
        });
        if (Object.keys(update).length) await s.ref.update(update);
      });
  }

  saved() {
    this.setItem(this.get('_itemData.$key'), this._itemData);
    this.afterSave();
    if (this.createItem) this.exitCreateItem();
  }

  async _saveUpdate(itemData, update, detail) {
    if (this.firestore) {
      // Strip out $ paths in a way that doesn't turn arrays into objects
      let itemDataToWrite = Path.copy(itemData);
      for (let key in itemDataToWrite) if (key[0] == '$') delete itemDataToWrite[key];

      let batch = firebase.firestore().batch();
      let collection = firebase.firestore().collection(this.dataPath);

      if (detail.deleting) {
        // Delete all the documents from subcollections
        if (this.subCollections) {
          for (let subCollectionKey of this.subCollections) {
            let subCollection = firebase.firestore().collection(`${this.dataPath}/${this.itemKey}/${subCollectionKey}`);
            while (true) {
              let s = await subCollection.limit(500).get();
              if (s.docs.length == 0) break;
              s.docs.forEach((doc) => batch.delete(doc.ref));
              await batch.commit();
              batch = firebase.firestore().batch();
            }
          }
        }
        batch.delete(collection.doc(this.itemKey));
      } else {
        // Remove previous key location if the key has changed.
        if (detail.keyChanged) {
          batch.delete(collection.doc(detail.previousKey));
        }

        // Update last modified.
        Path.set(itemDataToWrite, this.lastModifiedPath, firebase.firestore.FieldValue.serverTimestamp());

        // Set the Data
        batch.set(collection.doc(this.itemKey), itemDataToWrite);
      }

      await batch.commit();
    } else {
      // Strip out $ paths by flattening and rebuilding itemData.
      let itemDataToWrite = {};
      let flattenedItemData = Path.flattenObject(itemData, null, { validate: this.flattenedDataValidate.bind(this) });
      for (let path in flattenedItemData)
        if (!path.includes('$')) itemDataToWrite = Path.set(itemDataToWrite, path, flattenedItemData[path]).base;

      // Init update.
      update = update || {};

      // Remove previous key location if the key has changed.
      if (detail.keyChanged) update[Path.join([this.dataPath, detail.previousKey], '/')] = null;

      if (this.categoryDetails?.showList) {
        // If we are not deleting this item, update last modified.
        if (!detail.deleting) Path.set(itemDataToWrite, this.lastModifiedPath, firebase.database.ServerValue.TIMESTAMP);
      }

      update[Path.join([this.dataPath, detail.itemKey], '/')] = itemDataToWrite;
      await FirebaseWorker.ref()
        .update(update)
        .catch((err) => {
          console.log('ERR', err, update);
          throw err;
        });
    }
    this.saved();
  }

  scrollToStep(stepId) {
    let step = this.shadowRoot.querySelector(`katapult-editor-step[step="${stepId}"]`);
    if (step) this.scrollTo({ top: step.offsetTop, behavior: 'smooth' });
  }

  setItemData(itemData, makeReference) {
    this._setItemData(itemData);
    if (makeReference) {
      // Flatten and save the external item data as it looked when we started editing this item.
      // Later we can compare this with the current external item data to see if the item has been modifed while we've been editing it.
      this._setItemDataReference(Path.copy(itemData));
    }
  }

  setLabel(value) {
    this.set('_itemData.$label', value);
  }

  setSteps(steps) {
    if (Array.isArray(steps)) {
      let temp = steps
        .filter((step) => step.id != null)
        .map((step, i) => {
          if (typeof step === 'string') step = { name: step };
          step.index = i;
          step.active = step.if == null ? true : step.if();
          return step;
        });
      this.steps = temp;
    }
  }

  skipSteps() {
    this._setCurrentIndex(Infinity);
  }

  toAttributesArray(x) {
    return ToArray(Path.copy(x)).map((x) => {
      if (x.$key.startsWith('group')) {
        x.group = true;
        x.attributes = this.toAttributesArray(x.attributes);
        return x;
      } else {
        return { ...x, attribute: x.$key, value: x.$val ?? x.value };
      }
    });
  }

  getComputedLabel(data) {
    data ??= this._itemData;
    const { computedLabel } = this;
    if (!computedLabel) return { label: null, valid: true, values: [] };
    const { dependencies = [] } = computedLabel;
    const values = dependencies.map((x) => Path.get(data, x.path));
    const valid =
      dependencies.every((x, i) => {
        const optional = typeof x.optional === 'function' ? x.optional(data) : x.optional === true;
        const value = values[i];
        return optional || (value != null && value != '');
      }) ?? true;
    // Note: using || here because I don't want to accept empty strings.
    const label = computedLabel.compute?.(...values) || values.filter((x) => x).join(', ');
    return { label, valid, values };
  }

  checkLabelDependencies(changeRecord) {
    // Check if one of the label dependencies has changed.
    const dependencies = this.computedLabel?.dependencies;
    if (!Array.isArray(dependencies)) return;
    if (!dependencies.some((x) => Path.startsWith(Path.join(['_itemData', x.path]), changeRecord.path))) return;
    this.updateComputedLabel();
  }

  updateComputedLabel() {
    const { label, valid, values } = this.getComputedLabel();
    // Update properties.
    const update = {
      ['computedLabel.valid']: valid,
      ['computedLabel.values']: values,
      ['_itemData.$label']: label ?? `Untitled ${this.itemTypeTitles.title}`
    };
    this.setProperties(update);
  }

  updateCanSaveItem() {
    this.canSaveItem =
      this.unsavedChanges &&
      (!this.categoryDetails?.showList || (this.get('_itemData.$key') && !this.itemNewKeyExists && !this.labelContainsIllegalCharacters)) &&
      (!this.computedLabel?.dependencies?.length || this.computedLabel?.valid) &&
      this.itemIsValid();
  }

  /* Override */
  itemIsValid() {
    return true;
  }

  updateExternalChanges() {
    let res = this.compareFlattenedData(this.flattenedItemData, this.flattenedItemDataReference);
    if (res != null) this._setExternalChanges(res.changed);
  }

  updateFinishedSteps() {
    this.finishedSteps = this.currentIndex >= this.numSteps;
  }

  updateItemNewKeyExists() {
    let key = this.get('_itemData.$key');
    let prevKey = this.get('itemDataReference.$key');
    this.itemNewKeyExists =
      (this.get(`categoryData.${key}`) != null && key != prevKey) ||
      (this.computedLabel?.dependencies?.length &&
        this.computedLabel?.valid &&
        Object.values(this.categoryData ?? {}).some((item) => {
          return (
            item.$key != key && this.computedLabel?.dependencies?.every((x, i) => Path.get(item, x.path) == this.computedLabel?.values?.[i])
          );
        }));
  }

  updateLabelErrorMessage() {
    // If we force the key to be a derivative of the name, then check the key instead.
    let label = this.keyMatchLabel ? this.get('_itemData.$key') : this.get('_itemData.$label');
    if (!label || (this.computedLabel != null && !this.computedLabel.valid))
      this.labelErrorMessage = `Your ${this.itemTypeTitles.lowercaseTitle} must have a name`;
    else if (this.labelContainsIllegalCharacters)
      this.labelErrorMessage = `You may not use the following characters: ${IllegalCharacters.join(' ')}`;
    else if (this.itemNewKeyExists)
      this.labelErrorMessage = `${this.itemTypeTitles.article} ${this.itemTypeTitles.lowercaseTitle} with this name already exists`;
    else if (this._labelErrorMessage) this.labelErrorMessage = this._labelErrorMessage;
    else this.labelErrorMessage = '';
  }

  updateUnsavedChanges() {
    let res = this.compareFlattenedData(this.flattenedItemDataReference, this._flattenedItemData);
    if (res != null) this._setUnsavedChanges(res.changed);
  }

  getCreateItemButtonTitle(createItemButtonTitle, itemTypeTitle) {
    return createItemButtonTitle ?? `Finish Creating ${itemTypeTitle}`;
  }
}

customElements.define('katapult-editor-template', KatapultEditorTemplate);

export class AttributeTable extends KatapultElement {
  static get template() {
    return html`
      ${DefaultStyle}
      <style>
        :host {
          flex-grow: 1;
          max-width: 900px;
          display: block;
          color: var(--primary-text-color-faded);
        }
        #operatorToggle {
          text-transform: uppercase;
          padding: 2px 6px;
          border-radius: 12px;
          background: var(--secondary-color);
          color: var(--secondary-color-text-color);
          user-select: none;
          font-size: 10pt;
        }
      </style>
      <paper-table style="overflow: hidden;" rounded outline white>
        <paper-row short micro$="[[greaterThan(depth, 1)]]" grey divider>
          <paper-cell>Attribute</paper-cell>
          <paper-cell>Value</paper-cell>
          <template is="dom-if" if="[[showPicklists]]">
            <paper-cell>Picklist</paper-cell>
          </template>
          <template is="dom-if" if="[[logical]]">
            <paper-cell icon pointer on-click="toggleOperator">
              <div id="operatorToggle">[[logicalOperator]]</div>
            </paper-cell>
          </template>
          <template is="dom-if" if="[[!logical]]">
            <paper-cell icon></paper-cell>
          </template>
        </paper-row>
        <template is="dom-repeat" items="{{attributes}}" as="item">
          <paper-row expand divider>
            <template is="dom-if" if="[[item.group]]" restamp>
              <paper-cell>
                <attribute-table
                  attributes="{{item.attributes}}"
                  other-attributes="[[otherAttributes]]"
                  logical="[[logical]]"
                  depth="[[plusOne(depth)]]"
                  max-depth="[[maxDepth]]"
                  logical-operator="{{item.operator}}"
                  show-picklist-override="[[showPicklistOverride]]"
                  model-space-details="[[modelSpaceDetails]]"
                ></attribute-table>
              </paper-cell>
            </template>
            <template is="dom-if" if="[[!item.group]]" restamp>
              <paper-cell style$="[[getCellStyle(item.attribute, 'dropdown', otherAttributes)]]">
                <katapult-drop-down
                  label="Select an Attribute"
                  items="[[otherAttributes]]"
                  filter="[[boundFilterAttributeList]]"
                  label-function="[[camelCaseKey]]"
                  value-path="$key"
                  value="{{item.attribute}}"
                  no-label-float
                >
                </katapult-drop-down>
              </paper-cell>
              <paper-cell style$="[[getCellStyle(item.attribute, 'input', otherAttributes)]]">
                <input-element
                  style="flex-grow: 1;"
                  attribute_name="[[item.attribute]]"
                  hide-attribute-name
                  value="{{item.value}}"
                  model="[[getSub(otherAttributes, item.attribute)]]"
                  other-attributes="[[otherAttributes]]"
                  user-group="[[userGroup]]"
                  no-label-float
                  can-add-attribute
                  selected-picklist="[[item.picklist]]"
                  on-add-new-attribute="createNewPicklistItem"
                  camel-case
                  job-creator="[[modelSpaceDetails.$key]]"
                ></input-element>
              </paper-cell>
              <template is="dom-if" if="[[showPicklists]]">
                <paper-cell>
                  <template is="dom-if" if="[[getSub(otherAttributes, item.attribute, 'picklists')]]">
                    <katapult-drop-down
                      label="Picklist"
                      items="[[getSub(otherAttributes, item.attribute, 'picklists')]]"
                      label-function="[[camelCaseKey]]"
                      value-path="$key"
                      value="{{item.picklist}}"
                      no-label-float
                    ></katapult-drop-down>
                  </template>
                </paper-cell>
              </template>
            </template>
            <paper-cell icon-button>
              <katapult-button iconOnly noBackground noBorder icon="delete" on-click="removeAttribute"></katapult-button>
            </paper-cell>
          </paper-row>
        </template>
        <paper-row short micro$="[[greaterThan(depth, 1)]]">
          <paper-cell center pointer style="color: var(--paper-grey-600);" ripple on-click="addAttribute">
            <span>ADD ATTRIBUTE</span>
            <iron-icon style="margin-left: 8px;" icon="add-circle-outline"></iron-icon>
          </paper-cell>
          <template is="dom-if" if="[[logical]]">
            <template is="dom-if" if="[[greaterThan(maxDepth, depth)]]">
              <paper-cell center pointer style="color: var(--paper-grey-600);" ripple on-click="addGroup">
                <span>ADD GROUP</span>
                <material-icon style="margin-left: 8px;" icon="table_rows"></material-icon>
              </paper-cell>
            </template>
          </template>
        </paper-row>
      </paper-table>
    `;
  }

  static get properties() {
    return {
      attributes: Array,
      defaultOperator: {
        type: String,
        value: 'all'
      },
      depth: {
        type: Number,
        value: 1
      },
      otherAttributes: Object,
      logical: {
        type: Boolean,
        value: false
      },
      logicalOperator: {
        type: String,
        notify: true
      },
      maxDepth: {
        type: Number,
        value: Infinity
      },
      showPicklistOverride: Boolean
    };
  }

  static get observers() {
    return ['attributesChanged(attributes)', 'logicalOperatorChanged(logicalOperator)'];
  }

  ready() {
    super.ready();

    this.createNewPicklistItem = createNewPicklistItem;
    this.logicalOperatorChanged();
    this.boundFilterAttributeList = this.filterAttributeList.bind(this);
  }

  addAttribute(e) {
    this.push('attributes', {});
  }

  addGroup() {
    this.push('attributes', { group: true, operator: 'all' });
  }

  attributesChanged() {
    // Show the picklist column if any of the attributes has picklists
    const someAttrHasPicklist = this.attributes.some((x) => {
      const model = this.otherAttributes?.[x.attribute];
      return model?.picklists != null;
    });
    this.showPicklists = someAttrHasPicklist && !this.showPicklistOverride;
  }

  camelCaseKey(x) {
    if (x) return CamelCase(x.$key);
    return '';
  }

  createNewAttribute(e) {
    e.currentTarget.parentElement.close();
    document.dispatchEvent(new CustomEvent('create-new-attribute'));
  }

  greaterThan(a, b) {
    return a > b;
  }

  logicalOperatorChanged() {
    if (!['all', 'any'].includes(this.logicalOperator)) this.logicalOperator = this.defaultOperator;
  }

  plusOne(x) {
    return x + 1;
  }

  removeAttribute(e) {
    this.splice('attributes', e.model.index, 1);
  }

  toggleOperator() {
    if (this.logicalOperator == 'all') this.logicalOperator = 'any';
    else this.logicalOperator = 'all';
  }

  filterAttributeList(attribute) {
    return !['table', 'multi_dropdown'].includes(attribute?.gui_element) || !this.attributes.some((x) => x.attribute == attribute.$key);
  }

  getCellStyle(attribute, type, otherAttributes) {
    if (otherAttributes?.[attribute]?.gui_element == 'table') {
      if (type == 'dropdown') return 'flex-shrink: 0; flex-basis: calc(33% - 16px);';
      if (type == 'input') return 'flex-basis:auto;';
    }
    return '';
  }
}
customElements.define('attribute-table', AttributeTable);
